import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of, from, timer } from 'rxjs';
import { 
  map, 
  catchError, 
  exhaustMap, 
  switchMap, 
  tap, 
  mergeMap,
  take,
  filter,
  withLatestFrom
} from 'rxjs/operators';
import { Hub, HubCapsule, HubPayload } from '@aws-amplify/core';
import type { AuthUser } from '@aws-amplify/auth';

import { AuthActions } from './auth.actions';
import { AppState } from '../index';
import { selectLastProcessedUserId, selectIsAuthenticated, selectInitialAuthCheckCompleted } from './auth.selectors';
import { selectUrl, selectIsPublicRoute, selectQueryParams } from '../router/router.selectors';

import { STORAGE_KEYS } from '@app/core/constants/storage-keys.constants';
import { AmplifyService } from '@app/core/infrastructure/auth/amplify.service';
import { UserApiService } from '@app/core/api/user';
import { LoggerService, ToastNotificationService } from '@app/core/infrastructure';
import { UserData } from './models/auth.models';
import { UserActions } from '../user/user.actions';
import { LanguageActions } from '../language/language.actions';
import { UiActions } from '../ui/ui.actions';


@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly authAmplify = inject(AmplifyService);
  private readonly userApiService = inject(UserApiService);
  private readonly logger = inject(LoggerService).scope('AuthEffects');
  private readonly toastNotification = inject(ToastNotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService);


  constructor() {
    this.setupHubListener();
  }

  private clearAllStorage(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      this.logger.error('Error clearing browser storage:', e);
    }
  }

  private isAuthenticationError(error: any): boolean {
    const errorName = error?.name || '';
    const errorCode = error?.code || '';
    const errorMessage = error?.message || '';
    return (
      errorName === 'NotAuthorizedException' ||
      errorCode === 'NotAuthorizedException' ||
      errorMessage.includes('Access Token has been revoked') ||
      errorMessage.includes('Invalid Access Token') ||
      errorMessage.includes('Access token is expired') ||
      errorMessage.includes('Token is expired')
    );
  }

  private setupHubListener(): void {
    Hub.listen('auth', async (capsule: HubCapsule<string, HubPayload>) => {
      const { payload } = capsule;
      const userFromHub = payload.data as AuthUser;
      this.logger.debug(`Hub event: ${payload.event}`);
      this.ngZone.run(() => {
        switch (payload.event) {
          case 'signedIn':
            this.store.dispatch(AuthActions.hubSignedIn({ userId: userFromHub?.userId || '' }));
            break;
          case 'autoSignIn':
            this.store.dispatch(AuthActions.hubAutoSignIn({ userId: userFromHub?.userId || '' }));
            break;
          case 'signedOut':
            this.store.dispatch(AuthActions.hubSignedOut());
            break;
          case 'signInWithRedirect_failure':
          case 'autoSignIn_failure':
            this.logger.error('Hub Sign in failure:', payload.data);
            const errorData = payload.data as any;
            this.store.dispatch(AuthActions.hubSignInFailure({ 
              error: { 
                message: errorData?.message || 'Authentication failed',
                code: errorData?.code 
              } 
            }));
            break;
        }
      });
    });
  }

  checkInitialAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkInitialAuth),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectInitialAuthCheckCompleted)
      ),
      exhaustMap(([action, isAuthenticated, checkCompleted]) => {
        if (isAuthenticated && checkCompleted) {
          this.logger.debug('Auth already completed, skipping duplicate check');
          return of(AuthActions.initialAuthCheckCompleted());
        }
        return timer(500).pipe(
          switchMap(() => this.authAmplify.getCurrentUser()),
          switchMap(user => {
            if (user && user.userId) {
              return this.authAmplify.fetchUserAttributes().pipe(
                map(attributes => {
                  const userData: UserData = { 
                    ...attributes, 
                    sub: attributes.sub || user.userId,
                    email: attributes.email || ''
                  };
                  return AuthActions.initialAuthCheckSuccess({ 
                    user: userData, 
                    isAuthenticated: true 
                  });
                }),
                catchError(error => {
                  this.logger.error('Error fetching user attributes:', error);
                  if (this.isAuthenticationError(error)) {
                    this.logger.warn('Token revoked or invalid - triggering logout');
                    return of(AuthActions.logout({}));
                  }
                  return of(AuthActions.initialAuthCheckSuccess({ 
                    user: null, 
                    isAuthenticated: false 
                  }));
                })
              );
            } else {
              return of(AuthActions.initialAuthCheckSuccess({ 
                user: null, 
                isAuthenticated: false 
              }));
            }
          }),
          catchError(error => {
            this.logger.debug('Error during initial auth check:', error.message || error);
            return of(AuthActions.initialAuthCheckSuccess({ 
              user: null, 
              isAuthenticated: false 
            }));
          })
        );
      })
    )
  );

  initialAuthCheckSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initialAuthCheckSuccess),
      tap(({ user, isAuthenticated }) => {
        if (isAuthenticated && user) {
          this.authAmplify.startTokenExpiryMonitoring(() => {
            this.ngZone.run(() => {
              this.store.dispatch(AuthActions.logout({}));
            });
          });
          this.store.dispatch(AuthActions.syncUserWithBackend({ 
            userId: user.sub || '', 
            userAttributes: user 
          }));
        }
      }),
      map(() => AuthActions.initialAuthCheckCompleted())
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ credentials }) =>
        this.authAmplify.signIn(credentials.email, credentials.password).pipe(
          mergeMap((output) => {
            if (output.nextStep.signInStep === 'DONE') {
              return this.authAmplify.getCurrentUser().pipe(
                switchMap(user => this.authAmplify.fetchUserAttributes().pipe(
                  map(attributes => {
                    const userData: UserData = { 
                      ...attributes, 
                      sub: attributes.sub || user.userId,
                      email: attributes.email || ''
                    };
                    return AuthActions.loginSuccess({ user: userData });
                  })
                ))
              );
            } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
              return of(AuthActions.loginFailure({ 
                error: { 
                  message: 'CONFIRM_SIGN_UP: Please confirm your email address',
                  code: 'CONFIRM_SIGN_UP'
                } 
              }));
            } else {
              return of(AuthActions.loginFailure({ 
                error: { message: `Sign in incomplete: ${output.nextStep.signInStep}` } 
              }));
            }
          }),
          catchError(error => {
            this.logger.error('Login error:', error);
            return of(AuthActions.loginFailure({ 
              error: { 
                message: error.message || 'Login failed',
                code: error.code,
                name: error.name
              } 
            }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ user }) => {
        this.authAmplify.startTokenExpiryMonitoring(() => {
          this.ngZone.run(() => {
            this.store.dispatch(AuthActions.logout({}));
          });
        });
        const userId = user.sub || '';
        const email = user.email;
      }),
      map(({ user }) => AuthActions.syncUserWithBackend({ 
        userId: user.sub || '', 
        userAttributes: user 
      }))
    )
  );

  oAuthLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.oAuthLogin),
      exhaustMap(({ provider }) =>
        this.authAmplify.signInWithRedirect(provider).pipe(
          map(() => AuthActions.oAuthProcessingStarted()),
          catchError(error => {
            this.logger.error('OAuth login error:', error);
            return of(AuthActions.oAuthLoginFailure({ 
              error: { 
                message: error.message || 'OAuth login failed',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  hubSignedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hubSignedIn, AuthActions.hubAutoSignIn),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectLastProcessedUserId)
      ),
      filter(([action, isAuthenticated, lastProcessedUserId]) => {
        if (isAuthenticated) {
          this.logger.debug('Skipping Hub event - already authenticated');
          return false;
        }
        if (lastProcessedUserId === action.userId) {
          this.logger.debug('Skipping Hub event - user already processed:', action.userId);
          return false;
        }
        return true;
      }),
      exhaustMap(([action]) =>
        this.authAmplify.fetchUserAttributes().pipe(
          map(attributes => {
            const userData: UserData = { 
              ...attributes,
              sub: attributes.sub || action.userId,
              email: attributes.email || ''
            };
            return AuthActions.loginSuccess({ user: userData });
          }),
          catchError(error => {
            this.logger.error('Error processing Hub signedIn:', error);
            if (this.isAuthenticationError(error)) {
              this.logger.warn('Token revoked or invalid during Hub signedIn - triggering logout');
              return of(AuthActions.logout({}));
            }
            return of(AuthActions.hubSignInFailure({ 
              error: { message: error.message || 'Failed to fetch user data' } 
            }));
          })
        )
      )
    )
  );

  syncUserWithBackend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackend),
      exhaustMap(({ userId, userAttributes }) =>
        this.authAmplify.getIdToken().pipe(
          take(1),
          switchMap((token: string | null) => {
            if (!token) {
              throw new Error('No valid token available');
            }
            this.logger.debug('Syncing user with backend:', userId);
            return this.userApiService.createOrSyncMe();
          }),
          map(userProfile => {
            this.logger.debug('Backend user synced successfully');
            this.store.dispatch(UserActions.setProfile({ profile: userProfile }));
            const userData: UserData = { 
              ...userAttributes, 
              sub: userAttributes.sub || userId,
              email: userAttributes.email || '',
              backendUserId: userProfile.id,
              syncedAt: new Date().toISOString()
            };
            return AuthActions.syncUserWithBackendSuccess({ user: userData });
          }),
          catchError(error => {
            this.logger.error('Backend sync error:', error);
            return of(AuthActions.syncUserWithBackendFailure({ 
              error: { message: error.message || 'Backend sync failed', code: error.code } 
            }));
          })
        )
      )
    )
  );

  afterBackendSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackendSuccess),
      map(({ user }) => {
        this.logger.debug('Post-sync operations for user:', user.sub);
        return AuthActions.userBackendSyncCompleted();
      })
    )
  );

  navigateAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userBackendSyncCompleted),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectIsPublicRoute),
        this.store.select(selectUrl),
        this.store.select(selectQueryParams)
      ),
      filter(([_, isAuthenticated]) => isAuthenticated),
      tap(([_, __, isPublicRoute, currentUrl, queryParams]) => {
        const returnUrl = queryParams?.['returnUrl']
          || localStorage.getItem(STORAGE_KEYS.INVITATION_RETURN_URL);
        if (returnUrl) {
          localStorage.removeItem(STORAGE_KEYS.INVITATION_RETURN_URL);
          this.router.navigateByUrl(returnUrl);
        } else {
          const isOnRootRoute = currentUrl === '/' || currentUrl === '';
          const isOnInvitation = currentUrl?.startsWith('/invitation');
          if ((isPublicRoute || isOnRootRoute) && !isOnInvitation) {
            this.router.navigate(['/home']);
          }
        }
      })
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(() => {
        this.authAmplify.stopTokenExpiryMonitoring();
        this.store.dispatch(UserActions.clearProfile());
        this.store.dispatch(LanguageActions.resetLanguage());
        return from(this.authAmplify.signOut()).pipe(
          tap(() => {
            this.clearAllStorage();
            this.logger.debug('Logout successful - all storage cleared');
          }),
          map(() => AuthActions.logoutSuccess()),
          catchError(error => {
            this.logger.error('Logout error:', error);
            this.authAmplify.stopTokenExpiryMonitoring();
            this.clearAllStorage();
            return of(AuthActions.logoutSuccess());
          })
        );
      })
    )
  );

  navigateAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSuccess, AuthActions.hubSignedOut),
      switchMap(() => {
        this.store.dispatch(UserActions.clearProfile());
        return from(this.router.navigate(['/login'])).pipe(
          tap(() => this.store.dispatch(UiActions.hideGlobalLoading())),
          map(() => AuthActions.resetAuthState())
        );
      })
    )
  );

  hubSignedOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hubSignedOut),
      tap(() => {
        this.authAmplify.stopTokenExpiryMonitoring();
        this.clearAllStorage();
        this.store.dispatch(UserActions.clearProfile());
      }),
      map(() => AuthActions.resetAuthState())
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      exhaustMap(({ oldPassword, newPassword }) =>
        this.authAmplify.updatePassword(oldPassword, newPassword).pipe(
          map(() => {
            this.logger.debug('Password changed successfully');
            return AuthActions.changePasswordSuccess();
          }),
          catchError(error => {
            this.logger.error('Password change failed:', error);
            return of(AuthActions.changePasswordFailure({ 
              error: { 
                message: error.message || 'Failed to change password',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap(({ data }) =>
        this.authAmplify.signUp(data.email, data.password, data.email).pipe(
          map(() => {
            this.logger.debug('Registration successful');
            return AuthActions.registerSuccess({ email: data.email });
          }),
          catchError(error => {
            this.logger.error('Registration error:', error);
            let errorMessage = error.message || 'Registration failed';
            if (error.name === 'UsernameExistsException' || error.code === 'UsernameExistsException') {
              errorMessage = 'This email is already registered. If you signed up with Google or Facebook, please use that login method instead. Otherwise, try logging in or use the "Forgot Password" option.';
            }
            return of(AuthActions.registerFailure({ 
              error: { 
                message: errorMessage,
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  navigateAfterRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerSuccess),
      tap(({ email }) => {
        this.router.navigate(['/confirm-email'], { queryParams: { email } });
      })
    ),
    { dispatch: false }
  );

  confirmEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmEmail),
      exhaustMap(({ email, code }) =>
        this.authAmplify.confirmSignUp(email, code).pipe(
          map(() => {
            this.logger.debug('Email confirmed successfully');
            return AuthActions.confirmEmailSuccess({ email });
          }),
          catchError(error => {
            this.logger.error('Email confirmation error:', error);
            return of(AuthActions.confirmEmailFailure({ 
              error: { 
                message: error.message || 'Email confirmation failed',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  navigateAfterEmailConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmEmailSuccess),
      tap(() => {
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );

  resendConfirmationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendConfirmationCode),
      exhaustMap(({ email }) =>
        this.authAmplify.resendSignUpCode(email).pipe(
          map(() => {
            this.logger.debug('Confirmation code resent successfully');
            this.toastNotification.showSuccess(
              'Verification code sent! Please check your email inbox and spam folder.'
            );
            return AuthActions.resendConfirmationCodeSuccess();
          }),
          catchError(error => {
            this.logger.error('Resend confirmation code error:', error);
            return of(AuthActions.resendConfirmationCodeFailure({ 
              error: { 
                message: error.message || 'Failed to resend confirmation code',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordReset),
      exhaustMap(({ email }) =>
        this.authAmplify.resetPassword(email).pipe(
          map(() => {
            this.logger.debug('Password reset code sent successfully');
            return AuthActions.requestPasswordResetSuccess({ email });
          }),
          catchError(error => {
            this.logger.error('Password reset request error:', error);
            return of(AuthActions.requestPasswordResetFailure({ 
              error: { 
                message: error.message || 'Failed to request password reset',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  navigateAfterPasswordResetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordResetSuccess),
      tap(({ email }) => {
        this.toastNotification.showSuccess(
          'If an account exists with this email, you will receive a password reset code. Please check your inbox and spam folder.'
        );
        this.router.navigate(['/confirm-reset-password'], { 
          queryParams: { email } 
        });
      })
    ),
    { dispatch: false }
  );

  confirmPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmPasswordReset),
      exhaustMap(({ email, code, newPassword }) =>
        this.authAmplify.confirmResetPassword(email, code, newPassword).pipe(
          map(() => {
            this.logger.debug('Password reset confirmed successfully');
            return AuthActions.confirmPasswordResetSuccess();
          }),
          catchError(error => {
            this.logger.error('Confirm password reset error:', error);
            let message = error.message || 'Failed to confirm password reset';
            if (error.code === 'CodeMismatchException') {
              message = 'Invalid verification code. Please check and try again, or request a new code.';
            } else if (error.code === 'ExpiredCodeException') {
              message = 'Verification code has expired. Please request a new code.';
            } else if (error.code === 'LimitExceededException') {
              message = 'Too many attempts. Please try again later.';
            } else if (error.code === 'UserNotFoundException' || error.name === 'UserNotFoundException') {
              message = 'Account not found. Please check your email address or sign up for a new account.';
            }
            return of(AuthActions.confirmPasswordResetFailure({ 
              error: { message, code: error.code } 
            }));
          })
        )
      )
    )
  );

  navigateAfterPasswordResetConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmPasswordResetSuccess),
      tap(() => {
        this.toastNotification.showSuccess(this.translate.instant('toast.passwordResetSuccess'));
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );

  handleBackendSyncFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackendFailure),
      tap(({ error }) => {
        this.toastNotification.showError(this.translate.instant('toast.signInFailed'));
      }),
      map(() => AuthActions.logout({}))
    )
  );

  logoutAfterAccountDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteAccountSuccess),
      map(() => {
        this.logger.debug('Account deleted successfully, triggering logout');
        return AuthActions.logout({ skipTokenUnregister: true });
      })
    )
  );
}
